import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TagModule } from 'primeng/tag';

import {
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  ActionButton,
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';
import {
  ConfirmationButton,
  ConfirmationDialogComponent,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';

import {
  PROVIDER_COLUMNS,
  PROVIDER_SEARCH_CONFIG,
  getStatusDisplayText,
  getStatusSeverity,
} from './e-invoice-provider.config';
import {
  EInvoiceProvider,
  EInvoiceProviderPageRequest,
  EInvoiceProviderService,
} from './services/e-invoice-provider.service';
import { PermissionService } from '@app/core/services/permission.service';

@Component({
  selector: 'app-e-invoice-provider',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    TagModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './e-invoice-provider.component.html',
  styleUrls: [
    '../shared/e-invoice-table.css',
    './e-invoice-provider.component.css',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EInvoiceProviderComponent implements OnInit, OnDestroy {
  // Pagination
  first = 0;
  rows = 20;

  // Configuration
  readonly searchFormConfig = PROVIDER_SEARCH_CONFIG;
  readonly defaultColumns: TableColumn[] = PROVIDER_COLUMNS;
  readonly flexibleColumns: TableColumn[] = [];

  // Data signals
  dataSource = signal<EInvoiceProvider[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal<EInvoiceProviderPageRequest>({
    providerName: undefined,
    providerCode: undefined,
    status: undefined,
    page: 0,
    size: 20,
  });

  // Button configuration
  readonly primaryButton: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: false,
    icon: 'add',
  };

  readonly secondaryButton: ActionButton = {
    label: '',
    severity: 'success',
    outlined: true,
    show: true,
    icon: 'view_column',
  };

  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  confirmationTitle = signal('');
  confirmationMessage = signal('');
  confirmationButtons = signal<ConfirmationButton[]>([]);
  selectedProviderForAction = signal<EInvoiceProvider | null>(null);
  currentAction = signal<'delete' | 'status' | null>(null);

  private readonly destroy$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly providerService = inject(EInvoiceProviderService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.e_invoice_provider.edit');
  });

  ngOnInit(): void {
    this.loadProviders();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadProviders(): void {
    this.loading.set(true);

    this.providerService
      .searchProviders(this.searchParams())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.pageContent || []);
          this.totalRecords.set(response.totalElements || 0);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.provider.messages.error.failed_to_load',
            ),
          });
        },
      });
  }

  onSearch(searchData: SearchFormData): void {
    this.first = 0;
    this.searchParams.set({
      ...this.searchParams(),
      providerName: (searchData?.['providerName'] as string) || undefined,
      providerCode: (searchData?.['providerCode'] as string) || undefined,
      status:
        searchData?.['status'] != null
          ? (searchData['status'] as boolean)
          : undefined,
      page: 0,
    });
    this.loadProviders();
  }

  onReset(): void {
    this.first = 0;
    this.searchParams.set({
      providerName: undefined,
      providerCode: undefined,
      status: undefined,
      page: 0,
      size: this.rows,
    });
    this.loadProviders();
  }

  onPageChange(event: any): void {
    console.log('event', event);
    this.first = event.first;
    this.rows = event.rows;
    this.searchParams.set({
      ...this.searchParams(),
      page: event.first / event.rows,
      size: event.rows,
    });
    this.loadProviders();
  }

  // Status helpers
  getStatusSeverity(
    status: boolean,
  ): 'success' | 'warning' | 'danger' | 'secondary' {
    return getStatusSeverity(status);
  }

  getStatusDisplayText(status: boolean): string {
    return getStatusDisplayText(status, this.translateService);
  }

  onAdd(): void {
    this.router.navigate(['/configuration/e-invoice/provider/add']);
  }

  onView(provider: EInvoiceProvider): void {
    this.router.navigate([
      '/configuration/e-invoice/provider/view',
      provider.id,
    ]);
  }

  onEdit(provider: EInvoiceProvider): void {
    this.router.navigate([
      '/configuration/e-invoice/provider/edit',
      provider.id,
    ]);
  }

  onDelete(provider: EInvoiceProvider): void {
    this.selectedProviderForAction.set(provider);
    this.currentAction.set('delete');
    this.confirmationTitle.set(
      this.translateService.instant('common.confirmation.title'),
    );
    this.confirmationMessage.set(
      this.translateService.instant(
        'e_invoice.provider.messages.confirmation.delete',
        { name: provider.providerName || provider.providerCode || '' },
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onStatusChange(provider: EInvoiceProvider, newStatus: boolean): void {
    this.selectedProviderForAction.set(provider);
    this.currentAction.set('status');
    this.confirmationTitle.set(
      this.translateService.instant('common.confirmation.title'),
    );
    this.confirmationMessage.set(
      this.translateService.instant(
        newStatus
          ? 'e_invoice.provider.messages.confirmation.activate'
          : 'e_invoice.provider.messages.confirmation.deactivate',
        { name: provider.providerName || provider.providerCode || '' },
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          newStatus
            ? 'common.table.actions.activate'
            : 'common.table.actions.deactivate',
        ),
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onConfirmationDialogAction(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
      return;
    }

    if (action !== 'confirm') {
      this.closeConfirmationDialog();
      return;
    }

    const provider = this.selectedProviderForAction();
    const actionType = this.currentAction();

    if (!provider || !actionType) {
      this.closeConfirmationDialog();
      return;
    }

    if (actionType === 'delete') {
      this.handleDeleteProvider(provider);
      return;
    }

    if (actionType === 'status') {
      this.handleStatusChange(provider, !provider.status);
      return;
    }

    this.closeConfirmationDialog();
  }

  private handleDeleteProvider(provider: EInvoiceProvider): void {
    this.loading.set(true);
    this.providerService
      .deleteProvider(provider.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'e_invoice.provider.messages.success.deleted',
            ),
          });

          if (this.dataSource().length === 1 && this.first > 0) {
            this.first = Math.max(0, this.first - this.rows);
            this.searchParams.set({
              ...this.searchParams(),
              page: this.first / this.rows,
            });
          }

          this.closeConfirmationDialog();
          this.loadProviders();
        },
        error: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.provider.messages.error.failed_to_delete',
            ),
          });
        },
      });
  }

  private handleStatusChange(
    provider: EInvoiceProvider,
    newStatus: boolean,
  ): void {
    this.loading.set(true);

    this.providerService
      .updateProvider(provider.id, {
        providerCode: provider.providerCode,
        providerName: provider.providerName,
        note: provider.note,
        status: newStatus,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              newStatus
                ? 'e_invoice.provider.messages.success.activated'
                : 'e_invoice.provider.messages.success.deactivated',
            ),
          });
          this.closeConfirmationDialog();
          this.loadProviders();
        },
        error: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.provider.messages.error.failed_to_update_status',
            ),
          });
        },
      });
  }

  private closeConfirmationDialog(): void {
    this.showConfirmationDialog.set(false);
    this.selectedProviderForAction.set(null);
    this.currentAction.set(null);
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
